/**
 * What Android's hardware or gesture Back does in the app.
 *
 * Without a Back listener the Android WebView walks its own history, wherever the user is. Under
 * a paywall that cannot be closed that walks the page beneath it back to an earlier screen (a
 * lesson draws its header and its MIDI dialog over the paywall), and at the roadmap it does
 * nothing at all. The decision is a pure function of the app state so it can be tested on its
 * own; the hook in `Hooks/useAndroidBackButton` registers it on Android only.
 *
 * Kept free of React and redux, like the paywall signals in `Utils/paywallRequest`.
 */

/**
 * Leave the app in the background, go one history entry back, leave a lesson for the roadmap,
 * hand Back to the screen on screen (its own on-screen Back, see `takeOverBackOnScreen`), or close
 * the dialog, menu or panel on top (see `takeOverBackForLayer`).
 */
export type AndroidBackAction =
  | 'minimize'
  | 'go-back'
  | 'go-to-roadmap'
  | 'screen-back'
  | 'close-layer';

export interface AndroidBackState {
  /** The route on screen. */
  pathname: string;
  /** The WebView's own answer to whether there is an earlier history entry. */
  canGoBack: boolean;
  /**
   * A screen the user cannot close is on screen: the forced paywall and the other payment gates,
   * the offline screen, the device blocker, an enforced app update, the pending-deletion screen.
   */
  blockingGateShown: boolean;
  /** The screen on screen has handed in its own Back (see `takeOverBackOnScreen`). */
  screenHandlesBack: boolean;
  /** A dialog, menu or panel the user can close is open (see `takeOverBackForLayer`). */
  dismissibleLayerOpen: boolean;
}

/** Screens Back leaves the app from, as Android does for an app's root screen. */
const ROOT_PATHS = ['/roadmap'];
/**
 * Screens hardware Back leaves straight for the roadmap, wherever they were opened from, when the
 * screen has not handed in its own Back. The lesson hands in its on-screen Back while it is
 * mounted, so this is only the fallback for a press before it has.
 */
const ROADMAP_RETURN_PATHS = ['/lesson'];

export function androidBackActionFor(state: AndroidBackState): AndroidBackAction {
  if (state.blockingGateShown) return 'minimize';
  if (state.dismissibleLayerOpen) return 'close-layer';
  if (ROOT_PATHS.includes(state.pathname)) return 'minimize';
  if (state.screenHandlesBack) return 'screen-back';
  if (ROADMAP_RETURN_PATHS.includes(state.pathname)) return 'go-to-roadmap';
  return state.canGoBack ? 'go-back' : 'minimize';
}

interface ScreenBack {
  pathname: string;
  handleBack: () => void;
}

let screenBacks: ScreenBack[] = [];

/**
 * Called by a screen whose on-screen Back does more than leave (the lesson stops play and may
 * show its wrap-up), so hardware Back does the same. Only used while that screen's route is the
 * one on screen, and never over a blocking gate. Returns the function to call when the screen
 * goes away. The latest screen to hand one in wins, because two can overlap for a render while
 * the route changes.
 */
export function takeOverBackOnScreen(pathname: string, handleBack: () => void): () => void {
  const screenBack: ScreenBack = { pathname, handleBack };
  screenBacks = [...screenBacks, screenBack];
  return () => {
    screenBacks = screenBacks.filter((entry) => entry !== screenBack);
  };
}

/** The Back the screen at this route handed in, if it has. */
export function screenBackFor(pathname: string): (() => void) | undefined {
  return screenBacks.filter((entry) => entry.pathname === pathname).at(-1)?.handleBack;
}

interface DismissibleLayer {
  close: () => void;
  stackLevel: number;
}

/** The z-index MUI gives its dialogs, menus and popovers unless told otherwise. */
export const MUI_MODAL_STACK_LEVEL = 1300;

let dismissibleLayers: DismissibleLayer[] = [];

/**
 * Called by a dialog, menu, sheet or panel while it is open, with the close its own UI offers (its
 * X, Cancel or a tap outside), so hardware Back closes it and does nothing else on that press. Only
 * for layers the user can close: a gate that cannot be closed never hands one in.
 *
 * `stackLevel` is the z-index the layer is drawn at, so the layer on top closes first even when it
 * opened earlier (the tutorial video is drawn over the lesson's settings panel); between layers at
 * the same level the latest to open is on top. Returns the function to call when the layer closes
 * or goes away.
 */
export function takeOverBackForLayer(close: () => void, stackLevel: number): () => void {
  const layer: DismissibleLayer = {
    close: () => {
      dismissibleLayers = dismissibleLayers.filter((entry) => entry !== layer);
      close();
    },
    stackLevel,
  };
  dismissibleLayers = [...dismissibleLayers, layer];
  return () => {
    dismissibleLayers = dismissibleLayers.filter((entry) => entry !== layer);
  };
}

/**
 * The close of the layer on top, if one is open. Calling it also takes that layer off the stack at
 * once, so a second press before the layer has gone reaches what is beneath it instead of closing
 * the same layer twice.
 */
export function topLayerClose(): (() => void) | undefined {
  const onTop = dismissibleLayers.reduce<DismissibleLayer | undefined>(
    (top, layer) => (top === undefined || layer.stackLevel >= top.stackLevel ? layer : top),
    undefined,
  );
  return onTop?.close;
}

let blockingGatesShown = 0;

/**
 * Called by a gate the user cannot close when it comes on screen (through
 * `useBlockingGateHoldsBack`). Returns the function to call when it goes away. Counted, because
 * a gate is mounted per page and two pages can overlap for a render while the route changes, and
 * two different gates (the offline screen over the pending-deletion screen) can be up at once.
 */
export function holdBackForBlockingGate(): () => void {
  blockingGatesShown += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    blockingGatesShown -= 1;
  };
}

export function isBlockingGateShown(): boolean {
  return blockingGatesShown > 0;
}
